'use client';

import type posthog from 'posthog-js';

import { recordIntentSignalAction } from '~/lib/analytics/record-intent-signal.action';

/**
 * Returns the PostHog client if it has been initialized on the window,
 * or null if not available. This avoids hard-coupling to posthog-js at
 * import time so callers never throw when PostHog is absent.
 */
function getPostHog(): typeof posthog | null {
  if (typeof window === 'undefined') {
    return null;
  }

  // posthog-js attaches itself to window.posthog after init
  const ph = (window as unknown as Record<string, unknown>).posthog as
    | typeof posthog
    | undefined;

  if (!ph || typeof ph.capture !== 'function') {
    return null;
  }

  return ph;
}

/**
 * Reads the partial lead ID stored in sessionStorage after Step 1 completes.
 * Returns null if unavailable (anonymous visitor, sessionStorage blocked, etc.).
 */
function getStoredLeadId(verticalSlug: string): string | null {
  try {
    return sessionStorage.getItem(`leadgen_partial_lead_id:${verticalSlug}`);
  } catch {
    return null;
  }
}

/**
 * Persists an intent signal to the database for a known lead.
 * Fire-and-forget: errors are swallowed so they never affect UX.
 */
function persistIntentSignal(
  signalType: string,
  verticalSlug: string,
  metadata?: Record<string, unknown>,
): void {
  const leadId = getStoredLeadId(verticalSlug);

  if (!leadId) {
    // Anonymous visitor - skip DB recording per design decision
    return;
  }

  void recordIntentSignalAction({ signalType, leadId, verticalSlug, metadata });
}

interface BaseProperties {
  vertical: string;
  city: string;
  source: string;
  timestamp: string;
  experiment_variant?: string;
}

function baseProps(
  vertical: string,
  city: string,
  experimentVariant?: string,
): BaseProperties {
  return {
    vertical,
    city,
    source: typeof window !== 'undefined' ? window.location.href : '',
    timestamp: new Date().toISOString(),
    ...(experimentVariant ? { experiment_variant: experimentVariant } : {}),
  };
}

/**
 * Tracks the moment a user begins interacting with a lead capture form.
 */
export function trackFormStart(
  vertical: string,
  city: string,
  experimentVariant?: string,
): void {
  const ph = getPostHog();

  if (!ph) return;

  ph.capture('lead_form_start', baseProps(vertical, city, experimentVariant));
}

/**
 * Tracks completion of an individual form step (multi-step forms).
 */
export function trackStepComplete(
  vertical: string,
  city: string,
  stepIndex: number,
  stepName: string,
  experimentVariant?: string,
): void {
  const ph = getPostHog();

  if (!ph) return;

  ph.capture('lead_form_step_complete', {
    ...baseProps(vertical, city, experimentVariant),
    step_index: stepIndex,
    step_name: stepName,
  });
}

/**
 * Tracks successful form submission with the resulting lead ID.
 */
export function trackFormSubmit(
  vertical: string,
  city: string,
  leadId: string,
  experimentVariant?: string,
): void {
  const ph = getPostHog();

  if (!ph) return;

  ph.capture('lead_form_submit', {
    ...baseProps(vertical, city, experimentVariant),
    lead_id: leadId,
  });
}

/**
 * Tracks form abandonment, recording the last step the user reached.
 */
export function trackFormAbandon(
  vertical: string,
  city: string,
  lastStep: number,
  experimentVariant?: string,
): void {
  const ph = getPostHog();

  if (!ph) return;

  ph.capture('lead_form_abandon', {
    ...baseProps(vertical, city, experimentVariant),
    last_step: lastStep,
  });
}

/**
 * Tracks when a user engages with the ChatSuite widget (opens/interacts).
 */
export function trackChatWidgetEngaged(
  vertical: string,
  city: string,
  experimentVariant?: string,
): void {
  const ph = getPostHog();

  if (!ph) return;

  ph.capture(
    'chat_widget_engaged',
    baseProps(vertical, city, experimentVariant),
  );
}

/**
 * Tracks when a user clicks a predefined question chip in the chatbot section.
 */
export function trackChatbotChipClicked(
  vertical: string,
  city: string,
  chipText: string,
  experimentVariant?: string,
): void {
  const ph = getPostHog();

  if (!ph) return;

  ph.capture('chatbot_chip_clicked', {
    ...baseProps(vertical, city, experimentVariant),
    chip_text: chipText,
  });
}

/**
 * Tracks when the lead capture overlay is shown to a chatbot user.
 */
export function trackChatbotLeadOverlayShown(
  vertical: string,
  city: string,
  messageCount: number,
  experimentVariant?: string,
): void {
  const ph = getPostHog();

  if (!ph) return;

  ph.capture('chatbot_lead_overlay_shown', {
    ...baseProps(vertical, city, experimentVariant),
    message_count: messageCount,
  });
}

/**
 * Tracks successful lead capture from the chatbot overlay.
 */
export function trackChatbotLeadCaptured(
  vertical: string,
  city: string,
  leadId?: string,
  experimentVariant?: string,
): void {
  const ph = getPostHog();

  if (!ph) return;

  ph.capture('chatbot_lead_captured', {
    ...baseProps(vertical, city, experimentVariant),
    ...(leadId ? { lead_id: leadId } : {}),
  });
}

/**
 * Tracks when a user selects a care type on the Spitex landing page.
 * Also persists a care_type_selected intent signal to the DB when a leadId is available.
 */
export function trackCareTypeSelected(
  careType: string,
  verticalSlug: string,
): void {
  const ph = getPostHog();

  if (!ph) return;

  ph.capture('care_type_selected', { care_type: careType });
  persistIntentSignal('care_type_selected', verticalSlug, {
    care_type: careType,
  });
}

/**
 * Tracks when a user first interacts with the cost calculator.
 * Also persists a cost_calculator_used intent signal to the DB when a leadId is available.
 */
export function trackCostCalculatorUsed(
  canton: string,
  careType: string,
  hoursPerWeek: number,
  verticalSlug: string,
): void {
  const ph = getPostHog();

  if (!ph) return;

  ph.capture('cost_calculator_used', {
    canton,
    care_type: careType,
    hours_per_week: hoursPerWeek,
  });
  persistIntentSignal('cost_calculator_used', verticalSlug, {
    canton,
    care_type: careType,
    hours_per_week: hoursPerWeek,
  });
}

/**
 * Tracks when the comparison table section enters the viewport.
 * Also persists a comparison_table_viewed intent signal to the DB when a leadId is available.
 */
export function trackComparisonTableViewed(verticalSlug: string): void {
  const ph = getPostHog();

  if (!ph) return;

  ph.capture('comparison_table_viewed');
  persistIntentSignal('comparison_table_viewed', verticalSlug);
}

/**
 * Tracks when a user expands an FAQ item.
 * Also persists a case_study_view intent signal to the DB when a leadId is available.
 */
export function trackFaqExpanded(question: string, verticalSlug: string): void {
  const ph = getPostHog();

  if (!ph) return;

  ph.capture('faq_expanded', { question });
  persistIntentSignal('case_study_view', verticalSlug, { question });
}

/**
 * Tracks when a visitor actually starts (connects to) the Selenos voice
 * interviewer — the browser-side counterpart to `trackChatWidgetEngaged` for
 * the voice cohort. Fires once per conversation, on connect, so a denied mic
 * or an abandoned "tap to start" does not count as engagement.
 *
 * `sessionId` is the ElevenLabs conversation id, which is also the lead's
 * `web_session_id` merge key (the voice lead-tool binds it to
 * `system__conversation_id`). That makes it the join key back to the captured
 * lead, so experiment H7 can compare the voice cohort against the text cohort
 * on downstream qualification and pilot conversion. `leadId` is intentionally
 * absent here: the lead is created server-side only once the agent calls the
 * capture tool, so it does not exist at connect time — join via `sessionId`.
 */
export function trackVoiceDemoEngaged(sessionId: string): void {
  const ph = getPostHog();

  if (!ph) return;

  ph.capture('voice_demo_engaged', {
    sessionId,
    source: typeof window !== 'undefined' ? window.location.href : '',
    timestamp: new Date().toISOString(),
  });
}

/** Placement of a receipts surface on the Selenos homepage. */
export type ReceiptsPlacement = 'hero' | 'section' | 'pricing';

/**
 * Tracks when a receipts surface (the receipts section or one of the one-line
 * receipts) enters the viewport. Fired once per placement per page view
 * (experiment E1, homepage receipts layer).
 */
export function trackReceiptsViewed(placement: ReceiptsPlacement): void {
  const ph = getPostHog();

  if (!ph) return;

  ph.capture('selenos_receipts_viewed', {
    placement,
    source: typeof window !== 'undefined' ? window.location.href : '',
    timestamp: new Date().toISOString(),
  });
}

/**
 * Tracks a click on a one-line receipt anchor that jumps to the receipts
 * section (experiment E1, homepage receipts layer).
 */
export function trackReceiptLinkClicked(placement: ReceiptsPlacement): void {
  const ph = getPostHog();

  if (!ph) return;

  ph.capture('selenos_receipt_link_clicked', {
    placement,
    source: typeof window !== 'undefined' ? window.location.href : '',
    timestamp: new Date().toISOString(),
  });
}
